import { Box, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { Alert, Report, ReportInstance } from '../../../backendsdk';
import { useAlert } from '../../../hooks/alert';
import useApi from '../../../hooks/api';
import useCustomer from '../../../hooks/customer';
import useDevices from '../../../hooks/devices';
import useDrivers from '../../../hooks/drivers';
import { alertsPageDefs, reportsPageDefs } from '../../../utils/Pages';
import { DEFAULT_TITLE } from '../../Layout';
import AlertComponent from './AlertComponent';
import ReportContent from './ReportContent';
import ReportMenu from './ReportMenu';
import { AlertReport } from './ReportOptions';

export const EMPTY_REPORT: Report = {
    pk: 0,
    report_type: 'SAFETY_EVENTS',
    filters: {},
    is_report: true,
    report_scope: 'WEEKLY',
    report_day: 0,
    report_time: '00:00:00',
    active: true,
};

export const EMPTY_ALERT: Alert = {
    pk: 0,
    report_type: 'SAFETY_EVENTS',
    filters: {},
    is_report: false,
    cooldown_minutes: 15,
    method: 'EMAIL',
    active: true,
};

export const URL_PARAM_TO_REPORT_TYPE: Record<string, string> = {
    safety_events: 'SAFETY_EVENTS',
    geofence_events: 'GEOFENCE_EVENTS',
    device_wellness: 'DEVICE_WELLNESS',
    inactive_devices: 'INACTIVE_DEVICES',
    tasks: 'MALFUNCTIONS',
    accidents: 'ACCIDENTS_REPORT',
    safety_scores: 'DRIVER_SCORES',
    operational_data: 'OPERATION_REPORT',
    coaching_sessions: 'COACHING_SESSIONS',
    get_clip_completion: 'GET_CLIP_COMPLETION',
};

export const REPORT_TYPE_TO_URL_PARAM: Record<string, string> = Object.fromEntries(
    Object.entries(URL_PARAM_TO_REPORT_TYPE).map(([key, value]) => [value, key]),
);

/** Old dashboard URLs before COACHING_SESSIONS; redirect to `coaching_sessions`. */
const LEGACY_REPORT_URL_SEGMENTS = new Set(['pending_sessions', 'completed_sessions']);

interface ReportComponentProps {
    type: 'REPORTS' | 'ALERTS';
}

const ReportComponent: React.FC<ReportComponentProps> = (props: ReportComponentProps) => {
    const { reportType, instanceId } = useParams<{ reportType: string; instanceId: string }>();
    const history = useHistory();
    const location = useLocation();
    const [selectedReportType, setSelectedReportType] = React.useState<string | null>(
        !!reportType && Object.keys(URL_PARAM_TO_REPORT_TYPE).includes(reportType)
            ? URL_PARAM_TO_REPORT_TYPE[reportType]
            : null,
    );
    const [handlingStatusList, setHandlingStatusList] = useState<string[]>([]);
    const { devices } = useDevices();
    const { drivers } = useDrivers();
    const driverSubFleets = [...new Set(drivers.map((driver) => driver.sub_fleet))].sort((a, b) => a.localeCompare(b));
    const [initialInstance, setInitialInstance] = useState<ReportInstance>();
    const [isLoading, setIsLoading] = useState<boolean>(!!instanceId);
    const getReports = async () => {
        const res = await api.apiV0ReportGet();
        return res.data || [];
    };
    const { data: alertReports, isLoading: isLoadingReports } = useQuery({
        queryKey: ['reports'],
        queryFn: getReports,
    });
    const { customerUsers } = useCustomer();
    const [alertElement, setAlert] = useAlert();
    const { api, agencyApi } = useApi();
    const { t } = useTranslation();

    const isReports = props.type === 'REPORTS';

    const reports = alertReports?.reports || [];
    const alerts = alertReports?.alerts || [];

    const getGeofences = async () => {
        const res = await api.apiV0GeofenceGet();
        return res.data;
    };
    const { data: geofences } = useQuery({
        queryKey: ['geofences'],
        queryFn: getGeofences,
    });
    const geofenceIdToName: Record<string, string> = Object.fromEntries(
        (geofences || []).map((geofence) => [geofence.id.toString(), geofence.name]),
    );

    useEffect(() => {
        document.title = `${DEFAULT_TITLE} | ${t(`navigator.${(isReports ? reportsPageDefs : alertsPageDefs).name}`)}`;
    }, [isReports]);

    useEffect(() => {
        if (!isReports || !reportType || !LEGACY_REPORT_URL_SEGMENTS.has(reportType)) {
            return;
        }
        history.replace(
            `${location.pathname
                .replace('/pending_sessions', '/coaching_sessions')
                .replace('/completed_sessions', '/coaching_sessions')}${location.search}`,
        );
        if (!instanceId) {
            setSelectedReportType('COACHING_SESSIONS');
        }
    }, [isReports, reportType, instanceId, history, location.pathname, location.search]);

    useEffect(() => {
        if (instanceId) {
            return;
        }
        if (reportType && Object.keys(URL_PARAM_TO_REPORT_TYPE).includes(reportType)) {
            setSelectedReportType(URL_PARAM_TO_REPORT_TYPE[reportType]);
        } else if (!reportType) {
            setSelectedReportType(null);
        }
    }, [reportType, instanceId]);

    useEffect(() => {
        if (instanceId && !isNaN(parseInt(instanceId))) {
            api.apiV0ReportRunInstanceIdGet({ instanceId: parseInt(instanceId) }).then((res) => {
                setSelectedReportType(res.data.report_type);
                setInitialInstance(res.data);
                setIsLoading(false);
            });
        } else {
            setInitialInstance(undefined);
        }
    }, [instanceId]);

    useEffect(() => {
        agencyApi
            .agencyV1CustomerDataGet()
            .then((res) =>
                setHandlingStatusList(res.data.handling_status.map((status: { name: string }) => status.name)),
            );
    }, []);

    let content;
    if (isLoading) {
        content = (
            <Box
                sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
                <CircularProgress />
            </Box>
        );
    } else {
        if (!!selectedReportType) {
            content = isReports ? (
                <ReportContent
                    reportType={selectedReportType}
                    setSelectedReportType={setSelectedReportType}
                    devices={devices}
                    drivers={drivers}
                    driverSubFleets={driverSubFleets}
                    users={customerUsers}
                    geofences={geofenceIdToName}
                    handlingStatusList={handlingStatusList}
                    setHandlingStatusList={setHandlingStatusList}
                    setAlert={setAlert}
                    initialInstance={initialInstance}
                    scheduledReports={
                        reports.filter((report) => report.report_type === selectedReportType) as AlertReport[]
                    }
                    isLoadingScheduled={isLoadingReports}
                />
            ) : (
                <AlertComponent
                    alertType={selectedReportType}
                    setSelectedReportType={setSelectedReportType}
                    devices={devices}
                    drivers={drivers}
                    driverSubFleets={driverSubFleets}
                    users={customerUsers}
                    geofences={geofenceIdToName}
                    setAlert={setAlert}
                    scheduledAlerts={
                        alerts.filter((alert) => alert.report_type === selectedReportType) as AlertReport[]
                    }
                    isLoadingScheduled={isLoadingReports}
                />
            );
        } else {
            content = <ReportMenu type={props.type} setSelectedReportType={setSelectedReportType} />;
        }
    }

    return (
        <Box sx={{ width: '100%', maxWidth: '1200px', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {alertElement}
            {content}
        </Box>
    );
};

export default ReportComponent;
